import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh, triggerProgressRefresh } from "../../../shared/contexts/ProgressContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { ClosureApiService, CloseRegisteredClinicRequest } from "../../closure/api/ClosureApiService";
import { useUnsavedFormGuard } from "../../../shared/utils/unsavedChanges";

interface ClosureFormData {
  closure_reason: string;
  closure_date: string;
}

const INITIAL_FORM_DATA: ClosureFormData = {
  closure_reason: "",
  closure_date: "",
};

const ClinicClosureScreen: React.FC = () => {
  useStepRedirect(); // Fetch progress and handle redirects

  const [formData, setFormData] = useState<ClosureFormData>(INITIAL_FORM_DATA);
  // Ask before a design switch discards typed changes (both designs share this guard).
  useUnsavedFormGuard(formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const hasLoadedData = useRef(false);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, isLoading: progressLoading, hasClinic } = useProgressWithAutoRefresh();
  const closureApi = useMemo(() => new ClosureApiService(), []);

  const fromReview = !!(location.state as any)?.fromReview;

  // Pre-fill form data from existing closure request
  const closureRequest = progress?.active_requests?.find(
    (r: any) => r.request_type === "closure"
  );
  const showCancelInsteadOfBack =
    !fromReview && !!closureRequest && closureRequest.status === "draft";

  useEffect(() => {
    if (hasLoadedData.current || !progress || !closureRequest) return;
    hasLoadedData.current = true;

    const requestId = closureRequest.request_id;

    // Load existing closure data to pre-fill the form
    const loadData = async () => {
      try {
        const result = await closureApi.getClosureRequest(requestId);
        if (result.isSuccess && result.data) {
          const data = result.data as any;
          setFormData({
            closure_reason: data.closure_reason || "",
            closure_date: data.closure_date || "",
          });
        }
      } catch (error) {
        console.error("[ClinicClosureScreen] Failed to load existing data:", error);
      }
    };

    loadData();
  }, [closureRequest, progress, closureApi]);

  // Redirect if no registered clinic
  if (!progressLoading && !hasClinic) {
    navigate("/home", { replace: true });
    return null;
  }

  if (progressLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  const handleInputChange = (field: keyof ClosureFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // closure_date is optional but if provided should be valid and not in the past
    if (formData.closure_date) {
      const date = new Date(formData.closure_date);
      if (isNaN(date.getTime())) {
        newErrors.closure_date = t("invalid_date") || "Invalid date";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) {
          newErrors.closure_date = t("date_cannot_be_in_past") || "Date cannot be in the past";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmClosure = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const payload: CloseRegisteredClinicRequest = {};

      if (formData.closure_reason.trim()) {
        payload.closure_reason = formData.closure_reason.trim();
      }
      if (formData.closure_date) {
        payload.closure_date = formData.closure_date;
      }

      let result;
      if (closureRequest?.request_id) {
        result = await closureApi.updateRegisteredClosure(closureRequest?.request_id, payload);
      } else {
        result = await closureApi.closeRegisteredClinic(payload);
      }

      if (result.isSuccess && result.data) {
        skipNextStepRedirect();
        if (fromReview) {
          navigate("/closure-review");
        } else {
          navigate("/closure-documents", {
            state: {
              requestId: result.data.id,
              isExistingClinic: false,
            },
          });
        }
      } else {
        setErrors({ submit: result.error || t("failed_to_create_closure_request") || "Failed to create closure request" });
      }
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : t("failed_to_create_closure_request") || "Failed to create closure request",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    skipNextStepRedirect();
    navigate(fromReview ? "/closure-review" : "/clinic-management");
  };

  const handleCancelRequest = async () => {
    if (!closureRequest?.request_id) return;
    setShowCancelDialog(false);
    setCancelling(true);
    try {
      const result = await closureApi.cancelClosureRequest(closureRequest.request_id);
      if (result.isSuccess) {
        triggerProgressRefresh();
        navigate("/clinic-management", { replace: true });
      } else {
        setErrors({ submit: result.error || t("failed_to_cancel_request") || "Failed to cancel request" });
      }
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : t("failed_to_cancel_request") || "Failed to cancel request",
      });
    } finally {
      setCancelling(false);
    }
  };

  const continueButton = (
    <div className="flex gap-3 w-full">
      <button
        onClick={showCancelInsteadOfBack ? () => setShowCancelDialog(true) : handleBack}
        disabled={loading || cancelling}
        className={`min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold transition-all duration-200 ${
          showCancelInsteadOfBack
            ? "border border-red-300 text-red-600 hover:bg-red-50"
            : "border border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        {showCancelInsteadOfBack ? (t("cancel_request") || "Cancel Request") : (t("back") || "Back")}
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading
            ? "opacity-50 cursor-not-allowed"
            : "hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg"
        }`}
        style={{ backgroundColor: "#c03744" }}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            <span>{t("loading") || "Loading..."}</span>
          </div>
        ) : (
          <span>{closureRequest?.request_id ? (t("save_changes") || "Save Changes") : (t("request_closure") || "Request Closure")}</span>
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={showCancelInsteadOfBack ? () => setShowCancelDialog(true) : handleBack}
      showBackButton={true}
      showLanguageToggle={true}
      icon="❌"
      iconSize="w-24 h-24 text-6xl"
      title={t("close_clinic") || "Close Clinic"}
      subtitle=""
      actions={continueButton}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Error Message */}
        {errors.submit && (
          <div className="w-full rounded-xl p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-lg">⚠️</span>
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          </div>
        )}

        {/* Warning Message */}
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <span className="text-amber-500 text-2xl">⚠️</span>
            <div>
              <h4 className="font-semibold text-amber-800 mb-1">
                {t("closure_warning_title") || "Important Notice"}
              </h4>
              <p className="text-sm text-amber-700">
                {t("closure_warning_message") ||
                  "You are about to request closure of your registered clinic. This action will create a closure request that needs to be processed."}
              </p>
            </div>
          </div>
        </div>

        {/* Closure Form */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            {t("closure_details") || "Closure Details"}
          </h3>

          {/* Closure Reason */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("closure_reason") || "Reason for Closure"} ({t("optional") || "Optional"})
            </label>
            <textarea
              value={formData.closure_reason}
              onChange={(e) => handleInputChange("closure_reason", e.target.value)}
              placeholder={t("enter_closure_reason") || "Enter the reason for closing your clinic..."}
              rows={4}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.closure_reason
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.closure_reason && (
              <p className="text-red-500 text-sm mt-1">{errors.closure_reason}</p>
            )}
          </div>

          {/* Closure Date */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("closure_date") || "Closure Date"} ({t("optional") || "Optional"})
            </label>
            <input
              type="date"
              value={formData.closure_date}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => handleInputChange("closure_date", e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.closure_date
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.closure_date && (
              <p className="text-red-500 text-sm mt-1">{errors.closure_date}</p>
            )}
          </div>
        </section>
      </div>

      {/* Cancel Request Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-5xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
              {t("confirm_cancel_request") || "Cancel Request?"}
            </h3>
            <p className="text-gray-600 mb-6 text-sm text-center">
              {t("cancel_request_message") || "Are you sure you want to cancel this request? This action cannot be undone."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                {t("no_keep_it") || "No, Keep It"}
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center justify-center gap-2"
              >
                {cancelling && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                <span>{t("yes_cancel_it") || "Yes, Cancel It"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-5xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
              {closureRequest?.request_id
                ? (t("confirm_update_request") || "Confirm Update")
                : (t("confirm_closure_request") || "Confirm Closure Request")}
            </h3>
            <p className="text-gray-600 mb-6 text-sm text-center">
              {closureRequest?.request_id
                ? (t("confirm_update_closure_message") || "Are you sure you want to update your closure request?")
                : (t("confirm_closure_message") || "Are you sure you want to submit a closure request for this clinic? You will need to upload required documents after this step.")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                onClick={handleConfirmClosure}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all"
              >
                {t("confirm") || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveFormLayout>
  );
};

export default ClinicClosureScreen;
